// Aktueller Ablauf: docs/LERNABLAUF_AKTUELL.md. Ereignisse bleiben unverändert;
// simple-v1 ist eine Auswertungs-/Angebotsregel, keine weitere Kartenart.
import { derive, dayKey } from './learning.js';
import { attemptsOf, normalizeRating, policyOf, studyDay } from './learning-policy.js';

export const FLOW = 'simple-v1';
export function deriveSimple(classic, events, content, scheduler, quiet = new Set()) {
  const units = Object.fromEntries(content.units.map(u => [u.id, u]));
  const attempts = attemptsOf(events).filter(a => units[a.target] && (a.rev ?? 1) === (units[a.target].c1?.rev || 1));
  const reviews = [...classic];
  const introduced = {}, reveals = {}, last = {}, sentenceLast = {}, active = {}, usage = {};
  for (const a of attempts) {
    if (a.introducedAt !== null) introduced[a.target] = Math.min(introduced[a.target] ?? Infinity, a.introducedAt);
    if (a.revealedAt !== null || a.review) {
      const at = a.revealedAt ?? a.review.at;
      reveals[a.sentence] = Math.min(reveals[a.sentence] ?? Infinity, at);
    }
    if (!a.review || !a.skill) continue;
    const e = a.events.find(e => e.id === a.review.id);
    const rating = e.meta?.flow === FLOW ? e.rating : normalizeRating(a.skill, e.rating, a, policyOf(e.policy_version));
    reviews.push({ id: e.id, unit_id: a.target, sentence_id: a.sentence, direction: a.skill === 'p' ? 'p' : 'r', rating, reviewed_at: e.occurred_at });
  }
  const D = derive(reviews, scheduler, quiet);
  for (const [id, at] of Object.entries(introduced)) {
    const u = D.units[id] ||= {};
    u.introAt = Math.min(u.introAt ?? Infinity, at);
  }
  for (const e of reviews.sort((a,b) => a.reviewed_at - b.reviewed_at || a.id.localeCompare(b.id))) {
    const k = e.unit_id + ':' + e.direction;
    last[k] = e;
    sentenceLast[e.sentence_id] = e.reviewed_at;
    reveals[e.sentence_id] = Math.min(reveals[e.sentence_id] ?? Infinity, e.reviewed_at);
    usage[k + ':' + e.sentence_id] = e.reviewed_at;
  }
  // Cumulative active time per attempt, including interruptions and stories. Checkpoints
  // cannot double count the same seconds when a review is finally saved or synced.
  const durations = new Map();
  for (const e of events) if (e.attempt_id && e.active_ms >= 0) {
    const previous = durations.get(e.attempt_id);
    if (!previous || e.active_ms > previous.active_ms) durations.set(e.attempt_id, e);
  }
  for (const e of durations.values()) {
    const day = e.study_day || dayKey(e.occurred_at);
    active[day] = (active[day] || 0) + e.active_ms;
  }
  return Object.assign(D, { attempts, reveals, last, sentenceLast, active, usage });
}

// Immutable content indexes are reused on every card (also for the 2,500-goal course).
const indexes = new WeakMap();
function index(content) {
  if (!indexes.has(content)) indexes.set(content, { units: Object.fromEntries(content.units.map(u => [u.id,u])), sentences: Object.fromEntries(content.sentences.map(s => [s.id,s])), sorted: [...content.units].sort((a,b) => a.pos-b.pos) });
  return indexes.get(content);
}

export function simpleOverview(D, content, { now, tz, settings = {}, hidden = {u:{},s:{}}, startPos = 1, excluded = new Set(), extraNew = 0, extraMinutes = 0 } = {}) {
  const { units, sentences, sorted } = index(content), today = studyDay(now, tz);
  const known = id => !!hidden.u[id] || !!D.units[id]?.known;
  const introducedToday = Object.entries(D.units).filter(([id,u]) => !known(id) && u.introAt && studyDay(u.introAt,tz) === today).length;
  const newLimit = Math.max(0, (Number(settings.newPerDay) || 0) + extraNew - introducedToday);
  let availableNew = 0;
  const available = id => !!D.units[id]?.introAt || known(id) || units[id]?.pos < startPos;
  const lastFor = (id, skill) => D.last[id + ':' + (skill === 'p' ? 'p' : 'r')];
  const regular = [], retries = [], fresh = [], pending = [], waiting = [], blocked = [];
  let overdue = 0, dueTotal = 0, buried = 0;
  function pick(u, skill, role, previous) {
    let choices = (u.c1?.tasks || []).filter(x => x[skill]).map(x => ({...x, sentence: sentences[x.s]}))
      .filter(x => x.sentence && !hidden.s[x.s] && x.sentence.units.every(id => id === u.id || available(id)));
    if (role === 'retry') return choices.find(x => x.s === previous?.sentence_id) || choices.find(x => D.reveals[x.s] !== undefined) || choices[0] || null;
    // A first production task uses a sentence whose answer was already encountered.
    if (skill === 'p' && !D.units[u.id]?.p?.last) choices = choices.filter(x => D.reveals[x.s] !== undefined && studyDay(D.reveals[x.s], tz) < today);
    choices = choices.filter(x => D.sentenceLast[x.s] === undefined || studyDay(D.sentenceLast[x.s], tz) !== today);
    choices.sort((a,b) => (role === 'introduction' ? Number(b.s === u.main) - Number(a.s === u.main) : 0)
      || (D.usage[u.id + ':' + (skill === 'p' ? 'p' : 'r') + ':' + a.s] || 0) - (D.usage[u.id + ':' + (skill === 'p' ? 'p' : 'r') + ':' + b.s] || 0) || a.s.localeCompare(b.s));
    return choices[0] || null;
  }
  function task(u, skill, role, due, previous) {
    const x = pick(u, skill, role, previous);
    return x ? {target:u.id, kind:skill, role, sentence:x.s, ctx:x.ctx, rev:u.c1?.rev || 1, due} : null;
  }
  for (const u of sorted) {
    if (known(u.id)) continue;
    const state = D.units[u.id];
    if (!state?.last) {
      if (u.pos < startPos && !state?.introAt) continue;
      if (!state?.introAt && !state?.p?.last) {availableNew++; if(fresh.length >= newLimit + 5) continue;}
      const t = task(u, 'h', 'introduction', now);
      if (t && !excluded.has(u.id)) (state?.introAt || state?.p?.last ? pending : fresh).push(t);
      else if (!t && (state?.introAt || u.c1?.tasks.every(x => hidden.s[x.s]))) blocked.push(u.id);
      continue;
    }
    const seenToday = ['h','p'].some(sk => { const p = lastFor(u.id, sk); return p && studyDay(p.reviewed_at, tz) === today; });
    const choices = [];
    for (const skill of ['h','p']) {
      const card = skill === 'p' ? state.p : state;
      if (!card?.due) continue;
      const previous = lastFor(u.id, skill), retry = previous?.rating === 1;
      if (retry && card.due > now) { waiting.push(card.due); continue; }
      if (!retry && studyDay(card.due, tz) > today) continue;
      dueTotal++;
      if (studyDay(card.due, tz) < today) overdue++;
      if (excluded.has(u.id)) continue;
      if (!retry && seenToday) { buried++; continue; }
      const t = task(u, skill, retry ? 'retry' : 'regular', card.due, previous);
      if (!t) { blocked.push(u.id); continue; }
      if (retry) retries.push(t); else choices.push(t);
    }
    // Only one regular direction per goal/day. Oldest due wins; the other remains due.
    choices.sort((a,b) => a.due-b.due || (a.kind === 'p' ? -1 : 1));
    if (choices.length) regular.push(choices[0]);
  }
  regular.sort((a,b) => a.due-b.due); retries.sort((a,b) => a.due-b.due);
  const usedMs = D.active[today] || 0;
  const minutes = Math.max(1, Number(settings.dailyMinutes) || 15) + extraMinutes;
  const remainingMs = Math.max(0, minutes * 60000 - usedMs);
  const reviewMs = regular.reduce((n,t) => n + (t.kind === 'p' ? 15000 : 12000), 0) + (retries.length + waiting.length) * 10000;
  // Existing debt gets the time first. Reserve a minute per new goal, including a retry.
  const newSlots = overdue ? 0 : Math.min(newLimit, Math.floor(Math.max(0, remainingMs - reviewMs - pending.length * 30000) / 60000));
  return { regular, retries, fresh: fresh.slice(0,newSlots), pending, waiting, blocked: [...new Set(blocked)], overdue, dueTotal, buried,
    introducedToday, usedMs, remainingMs, minutes, stopped: remainingMs <= 0, brake: newSlots < newLimit && fresh.length > 0,
    nextAt: waiting.length ? Math.min(...waiting) : null, availableNew };
}

export function nextSimple(O) {
  if (O.stopped) return null;
  return O.retries[0] || O.regular[0] || O.pending[0] || O.fresh[0] || null;
}
